import { useCallback, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Scale, IdCard } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { fetchAppeals, type AdminAppeal } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A8.10 — Apellyatsiyalar navbati. GET /admin/appeals (backend `548626f`).
 * Userlar ban/strayk'ni shikoyat qiladi (POST /appeals — D-yo'li ulaydi).
 */
export function AppealsQueue() {
  const t = useT()
  const navigate = useNavigate()

  const rowActions = useMemo<RowAction<AdminAppeal>[]>(
    () => [
      {
        id: 'card',
        label: t.users.viewCard,
        icon: <IdCard size={13} />,
        onSelect: (r) => navigate(`/appeals/${r.id}`),
      },
    ],
    [t, navigate],
  )

  const columns = useMemo<AdminColumn<AdminAppeal>[]>(
    () => [
      { key: 'id', header: t.col.id, type: 'id', width: 70 },
      { key: 'created_at', header: t.col.time, type: 'date', width: 150 },
      {
        key: 'user_id',
        header: t.col.name,
        type: 'text',
        width: 150,
        sortable: false,
        render: (r) => (
          <Link to={`/users/${r.user_id}`} className="text-accent hover:underline">
            {r.username ? `@${r.username}` : `#${r.user_id}`}
          </Link>
        ),
      },
      {
        key: 'kind',
        header: t.appeals.kind,
        type: 'enum',
        width: 110,
        options: [
          { value: 'ban', label: t.appeals.kindBan, status: 'banned' },
          { value: 'strike', label: t.appeals.kindStrike, status: 'review' },
        ],
      },
      { key: 'reason', header: t.mod.reason, type: 'longtext', minWidth: 200 },
      {
        key: 'status',
        header: t.col.status,
        type: 'enum',
        width: 120,
        options: [
          { value: 'pending', label: t.mod.pending, status: 'pending' },
          { value: 'granted', label: t.appeals.granted, status: 'active' },
          { value: 'rejected', label: t.appeals.rejected, status: 'deleted' },
        ],
      },
    ],
    [t],
  )

  const fetcher = useCallback(fetchAppeals, [])

  return (
    <>
      <PageHeader title={t.appeals.queue} screenId="A8.10" description={t.appeals.queueNote} />
      <DataTable<AdminAppeal>
        screenId="A8.10"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Scale size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
